import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Referrer, ReferrerFormData, DealReferrer } from '../types';

interface ReferrerStore {
  referrers: Referrer[];
  loading: boolean;
  error: Error | null;
  dealReferrersLastUpdated: number | null;
  fetchReferrers: () => Promise<Referrer[]>;
  createReferrer: (referrer: ReferrerFormData) => Promise<Referrer | null>;
  updateReferrer: (id: string, referrer: Partial<ReferrerFormData>) => Promise<Referrer | null>;
  deleteReferrer: (id: string) => Promise<boolean>;

  // Deal referrer specific operations
  fetchDealReferrers: (dealId: string) => Promise<DealReferrer[]>;
  addDealReferrer: (dealId: string, referrerId: string, data?: Partial<DealReferrer>) => Promise<DealReferrer | null>;
  updateDealReferrer: (dealReferrerId: string, data: Partial<DealReferrer>) => Promise<DealReferrer | null>;
  removeDealReferrer: (dealReferrerId: string) => Promise<boolean>;
  triggerDealReferrersRefresh: () => void;

  // Partner integration
  syncFromPartner: (referrerId: string, partnerId: string) => Promise<Referrer | null>;
}

export const useReferrerStore = create<ReferrerStore>((set, get) => ({
  referrers: [],
  loading: false,
  error: null,
  dealReferrersLastUpdated: null,

  triggerDealReferrersRefresh: () => set({ dealReferrersLastUpdated: Date.now() }),

  fetchReferrers: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('referrers')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('full_name', { ascending: true });

      if (error) throw error;
      set({ referrers: data as Referrer[], loading: false });
      return data as Referrer[];
    } catch (error) {
      set({ error: error as Error, loading: false });
      return [];
    }
  },

  createReferrer: async (referrer: ReferrerFormData) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Add organization_id to referrer data
      const referrerData = {
        ...referrer,
        organization_id: profileData.organization_id
      };

      const { data, error } = await supabase
        .from('referrers')
        .insert([referrerData])
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      const currentReferrers = get().referrers;
      set({ 
        referrers: [...currentReferrers, data as Referrer],
        loading: false 
      });
      
      return data as Referrer;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return null;
    }
  },

  updateReferrer: async (id: string, referrer: Partial<ReferrerFormData>) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('referrers')
        .update(referrer)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      const currentReferrers = get().referrers;
      const updatedReferrers = currentReferrers.map(r => 
        r.id === id ? { ...r, ...data } as Referrer : r
      );
      
      set({ 
        referrers: updatedReferrers,
        loading: false 
      });
      
      return data as Referrer;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return null;
    }
  },

  deleteReferrer: async (id: string) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { error } = await supabase
        .from('referrers')
        .delete()
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (error) throw error;
      
      // Update local state
      const currentReferrers = get().referrers;
      set({ 
        referrers: currentReferrers.filter(r => r.id !== id),
        loading: false 
      });
      
      return true;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return false;
    }
  },

  fetchDealReferrers: async (dealId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('deal_referrers')
        .select(`
          *,
          referrer:referrer_id (*)
        `)
        .eq('deal_id', dealId);

      if (error) throw error;
      set({ loading: false });
      return data as DealReferrer[];
    } catch (error) {
      set({ error: error as Error, loading: false });
      return [];
    }
  },

  addDealReferrer: async (dealId: string, referrerId: string, data: Partial<DealReferrer> = {}) => {
    set({ loading: true, error: null });
    try {
      // organization_id is required: the commission engine filters by org and ignores NULL rows
      const { data: dealOrg, error: dealOrgError } = await supabase
        .from('deals')
        .select('organization_id')
        .eq('id', dealId)
        .single();

      if (dealOrgError || !dealOrg?.organization_id) {
        throw new Error('Unable to resolve deal organization for referrer');
      }

      // FIX 03/08/2026 (bug gruppi segnalatori, demo Marina/FinUp in call): qui NON si
      // pre-riempie più commission_rate. La vecchia risoluzione con p_service_id: null
      // congelava il default di gruppo (es. 20%) come snapshot su deal_referrers, e quello
      // snapshot vince su TUTTA la gerarchia — la rate specifica per servizio del gruppo
      // (es. 10% su FASCICOLO CHECK UP) non veniva mai più consultata dal motore.
      // Il rate resta NULL (= risoluzione live per-servizio) finché la firma non lo congela:
      // snapshot_deal_commission_rates ora fotografa CON il contesto servizio del deal.
      // Un valore esplicito passato in `data` (override manuale) continua a essere scritto.
      const dealReferrerData = {
        deal_id: dealId,
        referrer_id: referrerId,
        organization_id: dealOrg.organization_id,
        ...data
      };

      const { data: newDealReferrer, error } = await supabase
        .from('deal_referrers')
        .insert([dealReferrerData])
        .select(`
          *,
          referrer:referrer_id (*)
        `)
        .single();

      if (error) throw error;
      set({ loading: false });
      return newDealReferrer as DealReferrer;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return null;
    }
  },

  updateDealReferrer: async (dealReferrerId: string, data: Partial<DealReferrer>) => {
    set({ loading: true, error: null });
    try {
      // P12: nessun ricalcolo client-side dell'importo — al cambio di commission_rate il trigger
      // DB su deal_referrers notifica il motore in flusia-core, che riscrive la proiezione.
      const updatedData = { ...data };

      const { data: updatedDealReferrer, error } = await supabase
        .from('deal_referrers')
        .update(updatedData)
        .eq('id', dealReferrerId)
        .select(`
          *,
          referrer:referrer_id (*)
        `)
        .single();

      if (error) throw error;
      set({ loading: false });
      return updatedDealReferrer as DealReferrer;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return null;
    }
  },

  removeDealReferrer: async (dealReferrerId: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('deal_referrers')
        .delete()
        .eq('id', dealReferrerId);

      if (error) throw error;
      set({ loading: false });
      return true;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return false;
    }
  },

  // Sync referrer data from partner
  syncFromPartner: async (referrerId: string, partnerId: string) => {
    set({ loading: true, error: null });
    try {
      // Get partner data
      const { data: partner, error: partnerError } = await supabase
        .from('external_partners')
        .select('name, email, phone, tax_code, vat_number, commission_rate')
        .eq('id', partnerId)
        .single();

      if (partnerError) throw partnerError;
      if (!partner) throw new Error('Partner non trovato');

      // Update referrer with partner data
      const updateData = {
        full_name: partner.name,
        email: partner.email,
        phone: partner.phone,
        tax_code: partner.tax_code,
        vat_number: partner.vat_number,
        // la provvigione resta quella del profilo (si gestisce dalla scheda partner)
        updated_at: new Date().toISOString()
      };

      const { data: updatedReferrer, error: updateError } = await supabase
        .from('referrers')
        .update(updateData)
        .eq('id', referrerId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update local state
      const currentReferrers = get().referrers;
      const updatedReferrers = currentReferrers.map(r =>
        r.id === referrerId ? updatedReferrer : r
      );
      set({ referrers: updatedReferrers, loading: false });

      console.log('[ReferrerStore] Dati sincronizzati da partner:', referrerId);

      return updatedReferrer;
    } catch (error) {
      set({ error: error as Error, loading: false });
      return null;
    }
  }
}));